import React from "react";
import { useInView } from "react-intersection-observer";

import { Spacer } from "../Grid/Spacer";
import { Loader } from "../Loader";

export interface InfiniteLoaderProps {
  /** Whether there is a next page of data available */
  hasNextPage: boolean;
  /** Whether data is currently being loaded */
  loading: boolean;
  /** Function to fetch more results */
  fetchMoreResults: () => void;
  /** Size of the spacer elements */
  spacerSize?: "small" | "medium" | "large";
  /** The minimum time to show the loader before executing fetchMoreResults */
  minimumTime?: number;
}

/**
 * A component for displaying a loader to indicate more data is being loaded.
 * When it comes into view, it will show a loading indicator for minimumTime milliseconds
 * before executing the fetchMoreResults function.
 *
 * @param props - The component props
 * @returns A React element
 */
export function InfiniteLoader({
  hasNextPage,
  fetchMoreResults,
  loading,
  spacerSize = "medium",
  minimumTime = 2000,
}: InfiniteLoaderProps): React.ReactElement {
  const [ref, inView] = useInView();
  const inViewRef = React.useRef(inView);
  inViewRef.current = inView;

  React.useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    if (inView && hasNextPage && !loading) {
      timeoutId = setTimeout(async () => {
        // If the component is no longer in view, don't fetch more results. It
        // was not in view long enough to trigger the minimumTime.
        if (!inViewRef.current) {
          return;
        }
        fetchMoreResults();
      }, minimumTime);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [inView, hasNextPage, loading, fetchMoreResults, minimumTime]);

  if (!hasNextPage || loading) {
    return <></>;
  }

  return (
    <div ref={ref}>
      <div
        role="progressbar"
        aria-valuetext="Loading more items..."
        style={{ visibility: "initial" }}
      >
        <Spacer size={spacerSize} orientation="vertical" />
        <Loader horizontallyCentered />
        <Spacer size={spacerSize} orientation="vertical" />
      </div>
    </div>
  );
}
